import React, { useEffect, useMemo, useState } from 'react';
import CardBox from '@/pages/dashboard/components/CardBox';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { addChartLabel, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { useLocal, useOption, useWeekDayArr } from '@/pages/dashboard/utils/customHook';
import { commonValueNoThousandFormatter, formatPercentNum, getWeekDayTitle, handleDownload } from '@/pages/dashboard/utils/utils';
import { defaultSetOtherConfigFunc, getOptions, getRatioText } from '../../_config/utils';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import useTitleDes from '@/pages/dashboard/hooks/useTitleDes';
import { connect } from 'umi';
import TitleDes from '@/pages/dashboard/components/TitleDes';

function ActiveUsersActivityCo(props: any) {
  const { data: comdata, config: comConfig, explainInfo } = props || {};
  const { title, titleCN, titleDes: titleDesConfig, defaultAvtive = 1, resKey = 'active_user_activity', componentMetrics, isHaveSubTitle } = comConfig || {};
  const { locale, getLocalText, formatMessage, isEN } = useLocal('engagement_active_degree_biweekly');
  const { data: originData, loading, errorMsg: error } = comdata?.weekData || {};
  const [data, setData] = useState({
    [resKey]: [],
    time_range: '',
  });

  const [iseye, setIseye] = useState(true);
  const weekDayArr = useWeekDayArr();
  const subTitleTime = getWeekDayTitle(data?.time_range, weekDayArr, '~');

  const getConfig = (origin: any[], module: string) => {
    const formatterFunc = (params: any) => {
      let data: any[] = [];
      let title = '';
      if (Array.isArray(params)) {
        data = params?.map((item) => {
          title = getLocalText(`${module}_chart_title`, { day: item.name });
          // title = 'Login ' + item.name
          const name = getLocalText(`${module}_legend1`);
          const metricsNum = item?.value ? dataFormat({ value: item?.value, formatRule: 'thousandsInit'}) : '-';
          const target = origin?.find((originItem: { day: any; }) => originItem.day === item.axisValue);
          const ratioNum = target.this_change;
          const { text, status } = getRatioText(ratioNum, componentMetrics?.chartPercentDecimal, true);
          return {
            name,
            metricsNum,
            ratioNum: text,
            ratioStatus: status,
            color: item.color,
          };
        });
      } else {
        title = getLocalText(`${module}_chart_title`, { day: params.name });
        // title = 'Login ' + params.name + ' Active Users'
        data = [
          {
            name: params?.name,
            metricsNum: dataFormat({ value: params?.value, formatRule: 'thousandsInit'}),
            ratioNum: formatPercentNum(+origin?.[params.dataIndex]?.ratio, componentMetrics?.chartPercentDecimal ? componentMetrics?.chartPercentDecimal : 1),
            color: params.color,
          },
        ];
      }

      return getEchartTooltipText({
        title,
        metricsName: '',
        ratioName: '',
        hasRatio: true,
        data,
      });
    };

    return {
      bar: {
        formatterFunc,
        setOtherConfigFunc: (opt: any) => {
          defaultSetOtherConfigFunc({ opt, module, getLocalText });
          return opt;
        }
      },
      line: {
        formatterFunc,
        setOtherConfigFunc: (opt: any) => {
          defaultSetOtherConfigFunc({ opt, module, getLocalText });
          return opt;
        }
      },
      pie: {
        formatterFunc,
      },
    };
  };


  const { option, comTitle, config } = useMemo(() => {
    const option = getOptions(
      {
        x: 'day',
        legend: [
          {
            name: 'Biweekly Active Users',
            key: 'this_value',
          },
        ],
      },
      [
        {
          key: 'day',
          name: 'Day',
          header: getLocalText('activity_date'),
          render: (record: { day: string; }) => record.day?.replace('day', ' day')
        },
        {
          key: 'this_value',
          name: 'This Week',
          header: getLocalText('activity_legend1'),
          render: (record: { this_value: string | number; }) => dataFormat({ value: record.this_value, formatRule: 'thousandsInit'})
        },
      ],
      data?.[resKey],
    );
    const comTitle = isEN ? title : titleCN;
    const config = getConfig(data?.[resKey], 'activity');
    return {
      option,
      comTitle,
      config
    };
  }, [data, comConfig, isEN]);

  useEffect(() => {
    if (!originData) return;
    setData({
      [resKey]: originData?.[resKey]?.map?.((item: { day: number; this_value: string | number; }) => ({
        ...item,
        day: getLocalText('activity_day', { day: item.day, unit: item.day > 1 ? 'days' : 'day' }),
        this_value: commonValueNoThousandFormatter(componentMetrics?.type, item.this_value, componentMetrics?.decimal),
      })),
      time_range: originData?.time_range
    });
  }, [originData, locale]);


  const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(option);

  const renderList = [
    {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    }, {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <BarChart
        height={340}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? barSeries : []}
        formatterFunc={config?.bar?.formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          if (config?.bar?.setOtherConfigFunc) opt = config?.bar?.setOtherConfigFunc(opt);
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => (params.value)
          });
          opt = addChartLabel(opt, {
            formatter: (params: { value: string | number; }) => {
              const result = dataFormat({ value: params?.value, formatRule: 'thousandsInit'});
              return result;
            },
            show: iseye,
            formatNum: (value: string | number) => value
          });
          return opt;
        }}
      />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <LineChart
        height={340}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? lineSeries : []}
        formatterFunc={config?.line?.formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          if (config?.line?.setOtherConfigFunc) opt = config?.line?.setOtherConfigFunc(opt);
          opt = addChartLabel(opt, {
            formatter: (params: { value: string | number; }) => {
              const result = dataFormat({ value: params?.value, formatRule: 'thousandsInit'});
              return result;
            },
            show: iseye,
            formatNum: (value: string | number) => value
          });
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => (params.value)
          });
          return opt;
        }}
      />
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        height={340}
        loading={false}
        tableOption={option?.table?.option}
        tableList={option?.table?.data}
      />
    },
  ];

  const titleDes = useTitleDes({
    titleDesConfig,
    explainInfo,
    isEN,
  });

  return (
    <>
      <CardBox
        className='register-table'
        height={340}
        bordered={false}
        titleCom={
          <>
            {comTitle}
            <TitleDes titleDes={titleDes} />
          </>
        }
        subTitleCom={isHaveSubTitle ? <div className='register-table-time'>{subTitleTime}</div> : ''}
        renderList={renderList}
        defaultAvtive={defaultAvtive}
        setCurrentDataType={(type: any) => {
          if (type === 'eyes') {
            setIseye(!iseye);
          }
        }}
        downloadClick={async () => {
          handleDownload(option?.table, comTitle, false);
        }}
        isLoading={loading}
        isError={error}
        isNodata={isNodata}
      />
    </>
  );
}

export default connect((state: any) => ({
  explainInfo: state.dashboard?.explainInfo,
}))(ActiveUsersActivityCo);
